/**
 * Détection des lignes « avoir » dans les listes (facture / avoir partagent parfois le même flux UI).
 * Priorité : type AVOIR, flag API `statut.avoir`, puis titres historiques avoir.
 * Ne pas utiliser document_original_id seul : une facture convertie depuis BL/commande a cet id
 * et serait sinon exclue du tableau alors que le compteur API (total_items) reste correct.
 */

export function isStatutMarqueAvoir(statut: any): boolean {
    if (statut == null) {
        return false;
    }
    const v = statut.avoir;
    return v === true || v === 1 || v === '1';
}

const LEGACY_AVOIR_TITRES = ['Utilisé', 'Non utilisé', 'Partiellement utilisé'];

/** Répare les titres UTF-8 mal décodés (ex. « Non pay├® » → « Non payé »). */
export function repairStatutTitreEncoding(status: string): string {
    const raw = String(status || '').trim();
    if (!raw) {
        return raw;
    }
    if (/[├ÂÃ]/.test(raw)) {
        try {
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) {
                bytes[i] = raw.charCodeAt(i) & 0xff;
            }
            const decoded = new TextDecoder('utf-8').decode(bytes);
            if (decoded && !decoded.includes('\uFFFD')) {
                return decoded.trim();
            }
        } catch {
            /* ignore */
        }
        return raw
            .replace(/pay├®/gi, 'payé')
            .replace(/Pay├®/g, 'Payé')
            .replace(/├®/g, 'é')
            .replace(/Ã©/g, 'é')
            .replace(/Ã¨/g, 'è')
            .replace(/Ã /g, 'à');
    }
    return raw;
}

/** Normalise un titre de statut facture pour comparaison (casse / accents). */
export function normalizeFactureStatutTitre(status: string): string {
    const repaired = repairStatutTitreEncoding(status);
    const s = String(repaired || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    const map: Record<string, string> = {
        brouillon: 'Brouillon',
        paye: 'Payé',
        payee: 'Payé',
        'non paye': 'Non payé',
        'partiellement paye': 'Partiellement payé',
        annule: 'Annulé',
        annulee: 'Annulé',
    };
    return map[s] || String(repaired || '').trim();
}

const STATUT_ID_TO_FACTURE_TITRE: Record<number, string> = {
    1: 'Brouillon',
    5: 'Payé',
    6: 'Annulé',
    12: 'Non payé',
    15: 'Partiellement payé',
};

export function resolveFactureStatutTitre(d: any): string {
    const raw = d?.fullData ?? d;
    const sid = Number(raw?.statut_id ?? d?.statut_id);
    if (Number.isFinite(sid) && STATUT_ID_TO_FACTURE_TITRE[sid]) {
        return STATUT_ID_TO_FACTURE_TITRE[sid];
    }
    let status = repairStatutTitreEncoding(
        (raw?.statut?.titre ?? d?.status ?? raw?.status ?? '').toString().trim()
    );
    if (!status || status === 'Inconnu') {
        return status || '—';
    }
    return normalizeFactureStatutTitre(status);
}

export function isAvoirTableRow(d: any): boolean {
    const raw = d?.fullData ?? d;
    const typeTitre = (
        raw?.typeDocument?.titre ||
        raw?.type_document?.titre ||
        d?.typeDocument?.titre ||
        ''
    )
        .toString()
        .toUpperCase()
        .trim();
    if (typeTitre.includes('AVOIR')) {
        return true;
    }
    if (isStatutMarqueAvoir(raw?.statut)) {
        return true;
    }
    const status = (raw?.statut?.titre ?? d?.status ?? raw?.status ?? '').toString().trim();
    if (LEGACY_AVOIR_TITRES.includes(status)) {
        return true;
    }
    return false;
}

/** Facture « pure » : statut métier facture et pas une ligne avoir. */
export function isFactureTableRowExcludingAvoirs(d: any, factureStatuses: string[]): boolean {
    if (isAvoirTableRow(d)) {
        return false;
    }
    const status = resolveFactureStatutTitre(d);
    if (!status) {
        return false;
    }
    const allowed = (factureStatuses || []).map((s) => normalizeFactureStatutTitre(s));
    if (allowed.includes(status)) {
        return true;
    }
    // Repli : type FACTURE + statut_id connu (évite tableau vide alors que total_items API > 0)
    const raw = d?.fullData ?? d;
    const typeTitre = (raw?.typeDocument?.titre || '').toString().toUpperCase();
    const typeId = Number(raw?.type_document_id ?? raw?.typeDocument?.id);
    const isFactureType =
        typeTitre === 'FACTURE' ||
        typeTitre === 'FACTURE FOURNISSEUR' ||
        typeId === 11 ||
        typeId === 14;
    const sid = Number(raw?.statut_id ?? d?.statut_id);
    return isFactureType && Number.isFinite(sid) && !!STATUT_ID_TO_FACTURE_TITRE[sid];
}
